import React from "react";
import "./KaiAvatar.css";

/**
 * Kai — the KHub AI assistant mark. Direction "2a" from the design handoff.
 *
 * Kai = 海, "ocean": one orb holding concentric water arcs. The handoff's
 * prototype draws the arcs as CSS borders with only `border-bottom-color`
 * set; it also says to prefer a single inline SVG in production, which is
 * what this is — the arcs are real 90° bottom arcs rather than a border
 * trick, so they stay crisp at every size and need no `overflow: hidden`.
 *
 * Presentational only: it takes `state` and `size` and fetches nothing.
 *
 *   <KaiAvatar size={48} state="idle" animated />
 */

// Geometry, expressed against a 100×100 viewBox so one path set serves every
// size. From the handoff: arcs are 0.65×S and 0.40×S across, both centred on
// (50%, 54%) — slightly below the orb's middle, which is what gives the
// waterline its lift.
const ARC_CENTRE_Y = 54;
const OUTER_R = 32.5; // 0.65 x S / 2
const INNER_R = 20; //   0.40 x S / 2

/** The bottom 90° of a circle: from 45° to 135°, sweeping through the base. */
const bottomArc = (r) => {
  const k = Math.SQRT1_2 * r; // cos 45° = sin 45°
  return `M ${50 + k} ${ARC_CENTRE_Y + k} A ${r} ${r} 0 0 1 ${50 - k} ${ARC_CENTRE_Y + k}`;
};

/**
 * Stroke weight in px, per the handoff's size ladder. Converted to viewBox
 * units so the rendered stroke lands on the intended pixel width: the ladder
 * deliberately makes the stroke proportionally *heavier* as the orb shrinks,
 * so the waterline stays legible at 24px.
 */
const strokePx = (size) => (size >= 96 ? 4 : size >= 48 ? 3 : 2.5);

// Below 32px the inner arc muddies the read — the handoff drops it, and
// animation only runs at 48px and up.
const showsInnerArc = (size) => size >= 32;
const animates = (size) => size >= 48;

const KaiAvatar = ({ size = 48, state = "idle", animated = true, className = "", title }) => {
  const px = Number(size) || 48;
  const stroke = (strokePx(px) * 100) / px;
  const success = state === "success";
  const thinking = state === "thinking";
  // `thinking` is the one state whose entire job is motion, so it animates at
  // every size — the 26px header mark included. Idle keeps the handoff's
  // >=48px floor, where the tide arcs are large enough to read.
  const isAnimated = animated && (thinking || animates(px));

  // Unique per instance so two avatars on one page can't share a gradient id.
  const gid = React.useMemo(() => `kai-orb-${Math.random().toString(36).slice(2, 9)}`, []);

  const classes = [
    "kai-orb",
    `kai-orb-${state}`,
    isAnimated ? "kai-orb-animated" : "kai-orb-static",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={classes}
      style={{ width: px, height: px }}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" focusable="false">
        <defs>
          {/* Handoff: radial-gradient(circle at 50% 26%, ...) */}
          <radialGradient id={gid} cx="50%" cy="26%" r="75%">
            {success ? (
              <>
                <stop offset="0%" stopColor="#66e8e0" />
                <stop offset="55%" stopColor="#1bc5bd" />
                <stop offset="100%" stopColor="#0bb7af" />
              </>
            ) : (
              <>
                <stop offset="0%" stopColor="#78bdff" />
                <stop offset="54%" stopColor="#3699ff" />
                <stop offset="100%" stopColor="#187de4" />
              </>
            )}
          </radialGradient>
        </defs>

        <circle className="kai-orb-fill" cx="50" cy="50" r="50" fill={`url(#${gid})`} />

        {success && (
          /* Drawn-on check: stroke-dashoffset transition, per the handoff. */
          <polyline
            className="kai-orb-check"
            points="31,51 44,64 69,38"
            fill="none"
            stroke="#ffffff"
            strokeWidth={9}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {thinking && (
          <g className="kai-orb-thinking">
            {/* Expanding rings, 2.1s, staggered 0 / 0.7 / 1.4s. Three of them
                muddy the read below 32px, where one still says "working". */}
            {(showsInnerArc(px) ? [0, 1, 2] : [0]).map((i) => (
              <circle
                key={i}
                className={`kai-orb-ring kai-orb-ring-${i}`}
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke="rgba(255,255,255,.9)"
                strokeWidth={stroke * 1.3}
              />
            ))}
            {/* The "circulating" cue: one arc orbiting the mark at a constant
                rate. pathLength normalises the dash so the arc stays 22% of
                the circumference at any radius. Rotation only — no layout,
                no paint. */}
            <circle
              className="kai-orb-orbit"
              cx="50"
              cy="50"
              r="38"
              fill="none"
              stroke="rgba(255,255,255,.95)"
              strokeWidth={stroke * 1.15}
              strokeLinecap="round"
              pathLength="100"
              strokeDasharray="22 78"
            />
            <circle className="kai-orb-dot" cx="50" cy="50" r="5.5" fill="#ffffff" />
          </g>
        )}

        {!success && !thinking && (
          <>
            <path
              className="kai-orb-arc kai-orb-arc-outer"
              d={bottomArc(OUTER_R)}
              fill="none"
              stroke="rgba(255,255,255,.95)"
              strokeWidth={stroke}
              strokeLinecap="round"
            />
            {showsInnerArc(px) && (
              <path
                className="kai-orb-arc kai-orb-arc-inner"
                d={bottomArc(INNER_R)}
                fill="none"
                stroke="rgba(255,255,255,.6)"
                strokeWidth={stroke}
                strokeLinecap="round"
              />
            )}
          </>
        )}
      </svg>
    </span>
  );
};

export default KaiAvatar;
